import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">{title}</h2>
      <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed text-[15px]">{children}</div>
    </section>
  )
}

export default function Privacy() {
  usePageMeta(pageMeta('/privacy'))

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-2 text-gray-900 dark:text-white">Privacy Policy</h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 mb-10">Effective date: July 12, 2026</p>

        <Section title="1. Who we are">
          <p>
            Factory Nexus is operated by Factory Nexus by TynHub (&ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy
            explains what information we collect when you use the platform, how we use it, and the choices you
            have. Questions or requests go to{' '}
            <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-gray-800 dark:text-gray-200">Account information</strong> — name, email address, and password (stored hashed), or the profile details your OAuth provider shares when you sign in with GitHub or Google.</li>
            <li><strong className="text-gray-800 dark:text-gray-200">Project content</strong> — prompts, files, repositories you import, and the code agents generate for you.</li>
            <li><strong className="text-gray-800 dark:text-gray-200">Usage data</strong> — actions like project creation, agent runs, and API calls, used for metering plan limits and operating the service.</li>
            <li><strong className="text-gray-800 dark:text-gray-200">Technical data</strong> — logs and error reports (including IP address and browser information) used for security and debugging.</li>
          </ul>
        </Section>

        <Section title="3. GitHub data">
          <p>
            If you connect GitHub, we receive your profile and verified email via OAuth, and — only when you
            enable repository features — access to the repositories you choose. GitHub tokens are stored
            encrypted at rest and used solely to perform actions you initiate, such as importing a repository,
            pushing an agent&rsquo;s branch, or opening a pull request. We do not browse or copy repositories you
            have not connected. You can revoke access at any time from your GitHub settings.
          </p>
        </Section>

        <Section title="4. How we use your information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>to operate the platform: run agents, builds, tests, and reviews on your projects;</li>
            <li>to meter usage against your plan and process billing;</li>
            <li>to secure the service, prevent abuse, and debug failures;</li>
            <li>to send transactional email such as verification and billing notices.</li>
          </ul>
          <p>We do not sell your personal information or your code.</p>
        </Section>

        <Section title="5. AI providers">
          <p>
            When you start a task — an agent run, code review, chat message, or estimate — your prompt and the
            repository context relevant to that task are sent to third-party AI providers to generate the result.
            We currently use Anthropic, models routed through OpenRouter, and DigitalOcean serverless inference.
            Data is sent only for tasks you initiate, and Factory Nexus does not use your code or prompts to
            train models. See{' '}
            <Link to="/how-we-use-ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">How we use AI</Link>{' '}
            for a plain-language description.
          </p>
        </Section>

        <Section title="6. Payment information">
          <p>
            Payments are processed by Stripe. Your card details go directly to Stripe and are never stored on
            Factory Nexus servers. We keep subscription status and invoice records.
          </p>
        </Section>

        <Section title="7. Cookies and local storage">
          <p>
            We use browser local storage to keep you signed in (authentication tokens) and to remember interface
            preferences such as theme and mode. We do not use third-party advertising trackers.
          </p>
        </Section>

        <Section title="8. Retention">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Projects and account data are kept for as long as your account is active.</li>
            <li><strong className="text-gray-800 dark:text-gray-200">Anonymous projects</strong> (created without an account) are automatically and permanently deleted after 7 days.</li>
            <li>Logs and error reports are kept for a limited period for security and debugging, then discarded.</li>
          </ul>
        </Section>

        <Section title="9. Deletion and your rights">
          <p>
            You can delete individual projects at any time from the dashboard. To delete your account and its
            associated data, or to request a copy of the data we hold about you, email{' '}
            <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>{' '}
            from your account email. We will act on verified requests within 30 days, except where we must retain
            records for legal or billing reasons.
          </p>
        </Section>

        <Section title="10. Security">
          <p>
            We use encryption in transit (TLS) and at rest for sensitive credentials such as GitHub tokens and
            provider API keys, isolate agent execution, and restrict internal access to production data. No
            system is perfectly secure; if we learn of a breach affecting your data, we will notify you.
          </p>
        </Section>

        <Section title="11. Children">
          <p>
            The service is not directed at children and may not be used by anyone under 16. We do not knowingly
            collect data from children.
          </p>
        </Section>

        <Section title="12. Changes to this policy">
          <p>
            We may update this policy as the service evolves. For material changes we will give notice by email
            or in-product before the changes take effect. The effective date at the top always reflects the
            current version.
          </p>
        </Section>
      </div>

      <PublicFooter />
    </div>
  )
}
